/**
 * Supabase API functions for direct database operations
 * Used for fetching products and order details
 *
 * NOTE: For creating orders, use the useCreateOrder hook from @/api/hooks
 * which properly calls the Edge Function API.
 */

import { config } from "./config";
import * as Sentry from "@sentry/react";

/**
 * Get Supabase base URL from API base URL
 */
export function getSupabaseUrl(): string {
  const apiUrl = config.apiBaseUrl;
  try {
    const url = new URL(apiUrl);
    return `${url.protocol}//${url.host}`;
  } catch {
    return "http://localhost:54321";
  }
}

/**
 * Get Supabase anon key from environment
 */
function getSupabaseAnonKey(): string {
  const envKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (envKey) {
    return envKey;
  }
  return "";
}

/**
 * Get headers for Supabase REST API requests
 */
export function getSupabaseHeaders() {
  return {
    apikey: getSupabaseAnonKey(),
    Authorization: `Bearer ${getSupabaseAnonKey()}`,
    "Content-Type": "application/json",
    Prefer: "return=representation",
  };
}

/**
 * Product interface matching database schema
 */
export interface Product {
  id: string;
  retailer_id: string;
  name: string;
  description?: string;
  category?: string;
  price: number; // in cents (from database as numeric)
  stock: number;
  image_url?: string;
  active: boolean;
  created_at: string;
  updated_at?: string;
}

/**
 * Shipping method interface
 */
export interface ShippingMethod {
  name: string;
  description: string;
  price_cents: number;
}

/**
 * FAQ interface
 */
export interface FAQ {
  question: string;
  answer: string;
}

/**
 * Retailer config interface
 */
export interface RetailerConfig {
  faq?: FAQ[] | null;
  shipping_methods?: ShippingMethod[] | null;
  enforce_shipping?: boolean | null;
}

/**
 * Retailer interface from database
 */
export interface Retailer {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  logo_dark_mode: string | null;
  logo_size: number | null;
  primary_color: string | null;
  config: RetailerConfig | null;
}

/**
 * Fetch retailer config by retailer ID
 * @param retailerId - The retailer ID
 * @returns Retailer config object or null if not found
 */
export async function fetchRetailerConfig(retailerId: string): Promise<Retailer | null> {
  if (!retailerId) {
    throw new Error("Retailer ID is required to fetch retailer config");
  }

  const supabaseUrl = getSupabaseUrl();
  const url = `${supabaseUrl}/rest/v1/retailers?select=config&id=eq.${retailerId}&limit=1`;

  let response: Response;
  try {
    response = await fetch(url, {
      headers: getSupabaseHeaders(),
    });

    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(
        `Failed to fetch retailer config: ${response.status} ${response.statusText}. ${errorText}`
      );
      throw error;
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes("Failed to fetch retailer config")) {
      throw error;
    }
    throw error;
  }

  const data = await response.json();
  if (Array.isArray(data) && data.length > 0) {
    return data[0] as Retailer;
  }
  return null;
}

/**
 * Fetch products by retailer_id
 * @param retailerId - The retailer ID to fetch products for
 * @returns Array of products
 */
export async function fetchProductsByRetailerId(retailerId: string): Promise<Product[]> {
  const supabaseUrl = getSupabaseUrl();
  const url = `${supabaseUrl}/rest/v1/products?select=*&retailer_id=eq.${retailerId}&active=eq.true&order=name&limit=100`;

  let response: Response;
  try {
    response = await fetch(url, {
      headers: getSupabaseHeaders(),
    });

    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(
        `Failed to fetch products: ${response.status} ${response.statusText}. ${errorText}`
      );
      // Sentry.captureException(error, {
      //   tags: { component: "SupabaseApi", action: "fetchProductsByRetailerId", retailerId },
      //   extra: { url, status: response.status, statusText: response.statusText },
      // });
      throw error;
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes("Failed to fetch products")) {
      throw error;
    }
    // Sentry.captureException(error, {
    //   tags: {
    //     component: "SupabaseApi",
    //     action: "fetchProductsByRetailerId",
    //     retailerId,
    //     errorType: "network",
    //   },
    //   extra: { url },
    // });
    throw error;
  }

  const products = await response.json();
  return products;
}

/**
 * Shipping info stored in orders.shipping_info JSONB
 */
export interface ShippingInfo {
  name: string;
  email?: string | null;
  phone: string;
  address: string;
  city: string;
  province: string;
  postal_code: string;
}
/**
 * Metadata stored in orders.metadata JSONB
 */
export interface MetadataInfo {
  order_id: string;
  session_id: string | null;
}

/**
 * Order with items interface for display
 */
export interface OrderDetails {
  order: {
    id: string;
    retailer_id: string;
    customer_id: string | null;
    session_id: string | null;
    status: string;
    total_cents: number;
    currency: string;
    shipping: ShippingInfo;
    payment_id?: string | null;
    payment_provider?: string | null;
    brand_order_id?: string | null;
    metadata: MetadataInfo;
    created_at: string;
    updated_at: string;
    paid_at: string;
  };
  items: Array<{
    id: string;
    product_id: string;
    sku: string;
    title: string;
    quantity: number;
    price_cents: number;
    line_total_cents: number;
    created_at: string;
  }>;
}

/**
 * Update Order after successful payment
 * @param orderId - The order ID
 * @param paymentId - The payment reference from payment provider
 * @param paymentProvider - The payment provider (paystack/payfast)
 * @returns Success status
 */
export async function updateOrderAfterPayment(
  orderId: string,
  paymentId: string,
  paymentProvider: "paystack" | "payfast" = "paystack"
): Promise<void> {
  if (!orderId || !paymentId) {
    throw new Error("Order ID and payment reference are required");
  }

  const supabaseUrl = getSupabaseUrl();
  const headers = getSupabaseHeaders();

  // Update Order: status to 'paid', payment_id, payment_provider, paid_at
  const ordersUrl = `${supabaseUrl}/rest/v1/orders?id=eq.${orderId}`;
  let orderResponse: Response;
  try {
    orderResponse = await fetch(ordersUrl, {
      method: "PATCH",
      headers,
      body: JSON.stringify({
        status: "paid",
        payment_id: paymentId,
        payment_provider: paymentProvider,
        paid_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }),
    });

    if (!orderResponse.ok) {
      const errorText = await orderResponse.text();
      const error = new Error(
        `Failed to update order: ${orderResponse.status} ${orderResponse.statusText}. ${errorText}`
      );
      if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
        Sentry.captureException(error, {
          tags: { component: "SupabaseApi", action: "updateOrderAfterPayment", orderId, paymentId },
          extra: {
            url: ordersUrl,
            status: orderResponse.status,
            statusText: orderResponse.statusText,
          },
        });
      }
      throw error;
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes("Failed to update order")) {
      throw error;
    }
    if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
      Sentry.captureException(error, {
        tags: {
          component: "SupabaseApi",
          action: "updateOrderAfterPayment",
          orderId,
          paymentId,
          errorType: "network",
        },
        extra: { url: ordersUrl },
      });
    }
    throw error;
  }
}

/**
 * Fetch order details by order ID
 * @param orderId - The order ID
 * @returns Order details with items
 */
export async function fetchOrderDetails(orderId: string): Promise<OrderDetails> {
  if (!orderId) {
    throw new Error("Order ID is required");
  }

  const supabaseUrl = getSupabaseUrl();
  const headers = getSupabaseHeaders();

  // Fetch order
  const orderUrl = `${supabaseUrl}/rest/v1/orders?id=eq.${orderId}&select=*`;
  let orderResponse: Response;
  try {
    orderResponse = await fetch(orderUrl, {
      headers,
    });

    if (!orderResponse.ok) {
      const errorText = await orderResponse.text();
      const error = new Error(
        `Failed to fetch order: ${orderResponse.status} ${orderResponse.statusText}. ${errorText}`
      );
      if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
        Sentry.captureException(error, {
          tags: { component: "SupabaseApi", action: "fetchOrderDetails", orderId },
          extra: {
            url: orderUrl,
            status: orderResponse.status,
            statusText: orderResponse.statusText,
          },
        });
      }
      throw error;
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes("Failed to fetch order")) {
      throw error;
    }
    if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
      Sentry.captureException(error, {
        tags: {
          component: "SupabaseApi",
          action: "fetchOrderDetails",
          orderId,
          errorType: "network",
        },
        extra: { url: orderUrl },
      });
    }
    throw error;
  }

  const orderData = await orderResponse.json();
  const order = Array.isArray(orderData) ? orderData[0] : orderData;

  if (!order) {
    const error = new Error(`Order not found: ${orderId}`);
    if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
      Sentry.captureException(error, {
        tags: { component: "SupabaseApi", action: "fetchOrderDetails", orderId },
        extra: { url: orderUrl },
      });
    }
    throw error;
  }

  // Fetch order_items for this order
  const orderItemsUrl = `${supabaseUrl}/rest/v1/order_items?order_id=eq.${orderId}&select=*`;
  let orderItemsResponse: Response;
  try {
    orderItemsResponse = await fetch(orderItemsUrl, {
      headers,
    });

    if (!orderItemsResponse.ok) {
      const errorText = await orderItemsResponse.text();
      const error = new Error(
        `Failed to fetch order items: ${orderItemsResponse.status} ${orderItemsResponse.statusText}. ${errorText}`
      );
      if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
        Sentry.captureException(error, {
          tags: { component: "SupabaseApi", action: "fetchOrderDetails", orderId },
          extra: {
            url: orderItemsUrl,
            status: orderItemsResponse.status,
            statusText: orderItemsResponse.statusText,
          },
        });
      }
      throw error;
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes("Failed to fetch order items")) {
      throw error;
    }
    if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
      Sentry.captureException(error, {
        tags: {
          component: "SupabaseApi",
          action: "fetchOrderDetails",
          orderId,
          errorType: "network",
        },
        extra: { url: orderItemsUrl },
      });
    }
    throw error;
  }

  const orderItemsData = await orderItemsResponse.json();
  const orderItems = Array.isArray(orderItemsData) ? orderItemsData : [];

  // Transform order_items to expected format
  const items = orderItems.map(
    (item: {
      id: string;
      product_id: string;
      sku: string;
      title: string;
      quantity: number;
      price_cents: number;
      created_at?: string;
    }) => ({
      id: item.id,
      product_id: item.product_id,
      sku: item.sku,
      title: item.title,
      quantity: item.quantity,
      price_cents: item.price_cents,
      line_total_cents: item.price_cents * item.quantity,
      created_at: item.created_at || new Date().toISOString(),
    })
  );

  // Transform shipping_info to match expected format
  const shippingInfo = order.shipping_info || {};

  return {
    order: {
      id: order.id,
      retailer_id: order.retailer_id,
      customer_id: order.customer_id || null,
      session_id: order.session_id || null,
      status: order.status || "pending",
      total_cents: order.total_cents || 0,
      currency: order.currency || "ZAR",
      shipping: {
        name: shippingInfo.name || "",
        email: shippingInfo.email || null,
        phone: shippingInfo.phone || "",
        address: shippingInfo.address || "",
        city: shippingInfo.city || "",
        province: shippingInfo.province || "",
        postal_code: shippingInfo.postal_code || "",
      },
      payment_id: order.payment_id || null,
      payment_provider: order.payment_provider || null,
      brand_order_id: order.brand_order_id || null,
      metadata: {
        order_id: order.id,
        session_id: order.session_id || null,
      },
      created_at: order.created_at || new Date().toISOString(),
      updated_at: order.updated_at || new Date().toISOString(),
      paid_at: new Date().toISOString(),
    },
    items,
  };
}
